import { Link, useNavigate, useLocation } from "react-router-dom";
import { useContext, useState, useEffect, useMemo, useCallback  } from "react";
import { AuthContext } from "../../../contexts/AuthContext";
import { ServiceContext } from "../../../contexts/ServiceContext";
import { AppAlert } from "../../../utils/AppAlert";
import { ApiError } from "../../../services/ApiError";
import PhoneInput from 'react-phone-number-input';
import { ConfirmPhoneNumber } from '../../common' ;
import Select from "react-select";
import "./Navbar.css";

function Navbar() {
  const { isLoggedIn, logout } = useContext(AuthContext);
  const serviceCtx = useContext(ServiceContext);
  const [user, setUser] = useState(null);
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [phoneNumberConfirmed, setPhoneNumberConfirmed] = useState(false);
  const [companyId, setCompanyId] = useState(0);
  const [company, setCompany] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [isOpenConfirm, setIsOpenConfirm] = useState(false);
  const navigate = useNavigate();
  const [errorMessage, setErrorMessage] = useState("");
  const userRole = localStorage.getItem("user_role");
  const Global_Admin_Role = "Global Admin";
  const location = useLocation();
  
const customStyles = {
  control: (provided, state) => ({
    ...provided,
    backgroundColor: "#f5f5f5",
    borderColor: state.isFocused ? "#c7c4c4ff" : "#ccc",
    boxShadow: state.isFocused ? "0 0 0 2px #b1aeaeff" : "none",
    "&:hover": { borderColor: "#3f3f3fff" },
    borderRadius: "8px",
    padding: "0 4px",       
    minHeight: "45px",      
    width: "100%"
  }),
  valueContainer: (provided) => ({
    ...provided,
    height: "45px",         
    padding: "0 6px",
    alignItems: "center",  
    justifyContent: "center",  
    paddingBottom: "10px" 
  }),
  input: (provided) => ({
    ...provided,
    margin: 0,
    padding: 0,
    height: "100%"
  }),
  option: (provided, state) => ({
    ...provided,
    backgroundColor: state.isSelected
      ? "#525050"
      : state.isFocused
      ? "#ebe8e8ff"
      : "white",
    color: state.isSelected ? "white" : "#525050",
    padding: "5px 10px",   
    cursor: "pointer",
    fontWeight: state.isSelected ? "bold" : "normal",
    transition: "background-color 0.1s ease",
    ":active": {
      backgroundColor: "#525050",
      color: "white"
    }
  }),
  placeholder: (provided) => ({
    ...provided,
    color: "#888",
    fontStyle: "italic",
    textAlign: "center",
    margin: 0,
  }),
  singleValue: (provided) => ({
    ...provided,
    color: "#222",
    margin: 0,
  }),
  dropdownIndicator: (provided, state) => ({
    ...provided,
    color: state.isFocused ? "#3f3f3fff" : "#999",
    "&:hover": { color: "#3f3f3fff" }
  }),
  menu: (provided) => ({
    ...provided,
    borderRadius: "8px",
    overflow: "hidden",
    marginTop: "4px",
    boxShadow: "0 4px 12px #525050"
  }),
  menuList: (provided) => ({
    ...provided,
    padding: 0
  })
};

const roleOptions = useMemo(() => {
  if (userRole === Global_Admin_Role) {
    return [
      { value: "global_admin", label: "Global Admin" },
      { value: "company_admin", label: "Company Admin" },
    ];
  } else {
    return [
      { value: "company_admin", label: "Company Admin" },
    ];
  }
}, [userRole]);

  const onLogoutClick = async () => {
    try {
      logout();
      navigate("/login");
    } catch (error) {
      console.log("Error during logout:", error);
    }
  };

  const getUserIdFromToken = (token) => {
    if (!token) return null;
    try {
      const payload = token.split(".")[1];
      const decoded = JSON.parse(atob(payload));
      return decoded.user_id || null;
    } catch (e) {
      console.error("Invalid token:", e);
      return null;
    }
  };
  
    const fetchUser =  useCallback(async () =>  {
      if (!isLoggedIn || !serviceCtx || !serviceCtx.infrastructureService) return;

      setLoading(true);
      const token = localStorage.getItem("access_token");
      const userId = getUserIdFromToken(token);
      if (!userId) {
        setLoading(false);
        return;
      }

      try {
        const data = await serviceCtx.infrastructureService.get("User", { userId });
        setUser(data);
        setFirstName(data.firstName);
        setLastName(data.lastName);
        setCompanyId(data.companyId);
        setEmail(data.email);
        setPhoneNumber(data.phoneNumber);
        setPhoneNumberConfirmed(data.phoneNumberConfirmed);
        const firstRole = data.roles?.find((c) => c);
        setRole(firstRole);
        setRole(roleOptions.find((opt) => opt.label === firstRole));
        if(location.pathname === "/login"){ 
          navigate("/");
        }
      } catch (error) {
        console.error("Error fetching user:", error);
      }finally{
        setLoading(false);
      }
    }, [isLoggedIn, serviceCtx, roleOptions, location.pathname, navigate]);

  useEffect(() => {
    fetchUser();
  }, [fetchUser]);

  function validateUser({ firstName, lastName}) {
    if (!firstName || firstName.trim() === "") {
      setErrorMessage("First name is required");
      return false;
    }
    if (!lastName || lastName.trim() === "") {
      setErrorMessage("Last name is required");
      return false;
    } 
    if (!role) {
      setErrorMessage("Role is required");
      return false;
    }
    return true;
  }

  async function okClick() {
    let updateUser = {
      id: user.id,
      firstName: firstName,
      lastName: lastName,
      phoneNumber: phoneNumber,
      roles: [role.label]
    };
    if (!validateUser(updateUser)) return;
    try {
       setLoading(true);
       await serviceCtx.infrastructureService.put("User", updateUser);
       const token = localStorage.getItem("access_token");
       const userId = getUserIdFromToken(token);
       if (!userId) {
         setLoading(false);
         return;
       }
       
       const data = await serviceCtx.infrastructureService.get("User", { userId });
        setUser(data);
        setFirstName(data.firstName);
        setLastName(data.lastName);
        setCompanyId(data.companyId);
        setEmail(data.email);
        setPhoneNumber(data.phoneNumber);
        const firstRole = data.roles?.find((c) => c);
        setRole(firstRole);
        setRole(roleOptions.find((opt) => opt.label === firstRole));
        setIsEdit(false);
        setIsOpen(false);
        AppAlert.success("The user was changed successfully");
      } 
    catch (err) {
         if (err instanceof ApiError) {
          if (err.errors) {
            setErrorMessage(Object.values(err.errors).flat().join("\n"));
          } else {
            setErrorMessage(err.message);
          }
        } else {
          setErrorMessage(err.message || "Unexpected error");
        }
      }
    finally{
        setLoading(false);
        navigate("/");
    }
  } 

  async function confirmClick() {
    setIsOpen(false);
    setIsOpenConfirm(true);
  }  

   async function userInfoClick() {
    setLoading(true);
    try {
      fetchUser();
      if(user?.companyId !== 0){
         const comp = await serviceCtx.infrastructureService.get("Company", { companyId });
         setCompany(comp);
      }
    } catch (error) {
    } finally {
        setLoading(false);
        setIsOpen(true)
    }
  } 
  
  const handleConfirmChanged = () => {
  };

  return (
    <section className="navbar">
      {isLoggedIn ? (
        <div>
          <Link className="navbar-item" onClick={() => userInfoClick()}>
            <div className="user-nane-text">
              <div>
                {loading ? ( "Loading..." ) : user ? ( 
                  <>
                     <p className="user-nane-text-p"> {`${user.firstName ?? ""} ${user.lastName ?? ""}`.trim() || user.username}</p>
                     <p className="user-nane-text-p">{user.email}</p>
                  </>
                  ) : ( "No name"
               )}
              </div>
               <svg onClick={onLogoutClick} width="70" height="70"  viewBox="0 -4 18 32" fill="none" style={{ flexShrink: 0 }}>
                  <title>Logout</title>
                  <path stroke="#ffffffff" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2"
                        d="M20 12h-9.5m7.5 3l3-3-3-3m-5-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2h5a2 2 0 002-2v-1"/>
               </svg>
            </div>
          </Link>
          {isOpen && (
            <div style={{position: "fixed",top: 0,left: 0,right: 0,bottom: 0,display: "flex",justifyContent: "center",alignItems: "center",zIndex: 1000}}> 
              <div style={{backgroundColor: "white",borderRadius: "8px",boxShadow: "0 5px 15px rgba(0,0,0,0.3)", padding:"10px"}} className="modal-content-user-info">
                <div style={{background:"rgb(236, 236, 236)", color: "black", padding:"10px"}}>
                  <div style={{width:"100%"}}>
                    <div style={{display: "inline-block",  width: "calc(100% - 80px)"}}>
                      <h2 style={{ margin:"0px",padding:"13px 0px"}}>
                        {isEdit ? "User Settings" : "User Info"}
                      </h2>
                    </div>
                     {!isEdit && (
                       <div style={{display: "inline-block"}}>
                        <div style={{fontWeight:"normal", fontSize:"18px"}}>
                          <label style={{cursor: "pointer"}} onClick={() => setIsEdit(true)}>
                            <svg width="18" height="18" fill="currentColor" viewBox="0 0 16 16">
                              <path d="M8 4.754a3.246 3.246 0 1 0 0 6.492 3.246 3.246 0 0 0 0-6.492M5.754 8a2.246 2.246 0 1 1 4.492 0 2.246 2.246 0 0 1-4.492 0"/>
                              <path d="M9.796 1.343c-.527-1.79-3.065-1.79-3.592 0l-.094.319a.873.873 0 0 1-1.255.52l-.292-.16c-1.64-.892-3.433.902-2.54 2.541l.159.292a.873.873 
                                     0 0 1-.52 1.255l-.319.094c-1.79.527-1.79 3.065 0 3.592l.319.094a.873.873 0 0 1 .52 1.255l-.16.292c-.892 1.64.901 3.434 2.541 
                                     2.54l.292-.159a.873.873 0 0 1 1.255.52l.094.319c.527 1.79 3.065 1.79 3.592 0l.094-.319a.873.873 0 0 1 1.255-.52l.292.16c1.64.893 
                                     3.434-.902 2.54-2.541l-.159-.292a.873.873 0 0 1 .52-1.255l.319-.094c1.79-.527 1.79-3.065 0-3.592l-.319-.094a.873.873 0 0 
                                     1-.52-1.255l.16-.292c.893-1.64-.902-3.433-2.541-2.54l-.292.159a.873.873 0 0 1-1.255-.52zm-2.633.283c.246-.835 1.428-.835 
                                     1.674 0l.094.319a1.873 1.873 0 0 0 2.693 1.115l.291-.16c.764-.415 1.6.42 1.184 1.185l-.159.292a1.873 1.873 0 0 0 1.116 
                                     2.692l.318.094c.835.246.835 1.428 0 1.674l-.319.094a1.873 1.873 0 0 0-1.115 2.693l.16.291c.415.764-.42 1.6-1.185 1.184l-.291-.159a1.873 
                                     1.873 0 0 0-2.693 1.116l-.094.318c-.246.835-1.428.835-1.674 0l-.094-.319a1.873 1.873 0 0 0-2.692-1.115l-.292.16c-.764.415-1.6-.42-1.184-1.185l.159-.291A1.873 
                                     1.873 0 0 0 1.945 8.93l-.319-.094c-.835-.246-.835-1.428 0-1.674l.319-.094A1.873 1.873 0 0 0 3.06 4.377l-.16-.292c-.415-.764.42-1.6 
                                     1.185-1.184l.292.159a1.873 1.873 0 0 0 2.692-1.115z"/>
                            </svg> Edit
                          </label>
                        </div>
                       </div>
                     )}
                  </div>
                </div>
                <div style={{border: "solid rgb(236, 236, 236) 1px", padding:"10px", color: "black", textAlign: "start"}}>
                  <div>
                    {isEdit && (
                      <div>
                        <div>
                          <div className="inputs" style={{marginTop:"10px"}}>
                            <div className="input">
                              <svg width="20" height="20" fill="#797979" viewBox="0 0 24 24"> 
                                <g stroke="none" stroke-width="1" fill="none" fill-rule="evenodd">
                                  <g fill="#797979" fill-rule="nonzero">
                                    <path d="M18.75,3 C20.5449254,3 22,4.45507456 22,6.25 L22,17.75 C22,19.5449254 
                                             20.5449254,21 18.75,21 L5.25,21 C3.45507456,21 2,19.5449254 2,17.75 
                                             L2,6.25 C2,4.45507456 3.45507456,3 5.25,3 L18.75,3 Z M18.75,4.5 L5.25,4.5 
                                             C4.28350169,4.5 3.5,5.28350169 3.5,6.25 L3.5,17.75 C3.5,18.7164983 
                                             4.28350169,19.5 5.25,19.5 L18.75,19.5 C19.7164983,19.5 20.5,18.7164983 
                                             20.5,17.75 L20.5,6.25 C20.5,5.28350169 19.7164983,4.5 18.75,4.5 Z 
                                             M6.74835407,6.5 L17.2543007,6.5 C17.6685143,6.5 18.0043007,6.83578644 
                                             18.0043007,7.25 C18.0043007,7.62969577 17.7221468,7.94349096 17.3560713,7.99315338 
                                             L17.2543007,8 L6.74835407,8 C6.33414051,8 5.99835407,7.66421356 5.99835407,7.25 
                                             C5.99835407,6.87030423 6.28050795,6.55650904 6.64658351,6.50684662 
                                             L6.74835407,6.5 L17.2543007,6.5 L6.74835407,6.5 Z">
                                    </path>
                                  </g>
                                </g>
                              </svg>
                              <input placeholder="First name" onChange={(e) => setFirstName(e.target.value)} value={firstName}/>
                            </div>
                            <div className="input">
                               <svg width="20" height="20" fill="#797979" viewBox="0 0 24 24">
                                <g stroke="none" stroke-width="1" fill="none" fill-rule="evenodd">
                                  <g fill="#797979" fill-rule="nonzero">
                                    <path d="M18.75,3 C20.5449254,3 22,4.45507456 22,6.25 L22,17.75 C22,19.5449254 
                                             20.5449254,21 18.75,21 L5.25,21 C3.45507456,21 2,19.5449254 2,17.75 
                                             L2,6.25 C2,4.45507456 3.45507456,3 5.25,3 L18.75,3 Z M18.75,4.5 L5.25,4.5 
                                             C4.28350169,4.5 3.5,5.28350169 3.5,6.25 L3.5,17.75 C3.5,18.7164983 
                                             4.28350169,19.5 5.25,19.5 L18.75,19.5 C19.7164983,19.5 20.5,18.7164983 
                                             20.5,17.75 L20.5,6.25 C20.5,5.28350169 19.7164983,4.5 18.75,4.5 Z 
                                             M6.74835407,6.5 L17.2543007,6.5 C17.6685143,6.5 18.0043007,6.83578644 
                                             18.0043007,7.25 C18.0043007,7.62969577 17.7221468,7.94349096 17.3560713,7.99315338 
                                             L17.2543007,8 L6.74835407,8 C6.33414051,8 5.99835407,7.66421356 5.99835407,7.25 
                                             C5.99835407,6.87030423 6.28050795,6.55650904 6.64658351,6.50684662 
                                             L6.74835407,6.5 L17.2543007,6.5 L6.74835407,6.5 Z">
                                    </path>
                                  </g>
                                </g>
                               </svg>
                               <input placeholder="Last name" onChange={(e) => setLastName(e.target.value)} value={lastName}/>
                            </div>
                            <div className="input">
                              <svg width="20" height="20" fill="#797979" viewBox="0 0 16 16">
                                <path d="M.05 3.555A2 2 0 0 1 2 2h12a2 2 0 0 1 1.95 1.555L8 8.414zM0 
                                         4.697v7.104l5.803-3.558zM6.761 8.83l-6.57 4.027A2 2 0 0 0 2 14h12a2 
                                         2 0 0 0 1.808-1.144l-6.57-4.027L8 9.586zm3.436-.586L16 11.801V4.697z"/>
                              </svg>
                              <input type="email" placeholder="Email" disabled={true} onChange={(e) => setEmail(e.target.value)} value={email}/>
                            </div>
                            <div className="input">
                              <div style={{paddingLeft: "30px"}}>
                                <PhoneInput placeholder="Phone number " defaultCountry="US" enableSearch={true} value={phoneNumber} onChange={setPhoneNumber}/>
                              </div>
                            </div>
                            <div className="input settings-select">
                              <div className="select-company-u">
                                 <Select isSearchable={true} placeholder="Please select a role" styles={customStyles} options={roleOptions} value={role} 
                                         onChange={(selectedOption) => setRole(selectedOption)} isDisabled={true}/>
                              </div>
                            </div>
                            {errorMessage && (
                              <div className="error input">
                                <span style={{paddingLeft: "10px"}}>{errorMessage}</span>
                              </div>
                            )}
                          </div>
                        </div>
                        <div className="submit-container" style={{padding:"0px 10px", margin: "20px 0px"}}>
                          <div className="cancel-user-info" onClick={() => setIsEdit(false)}>Cancel</div>
                          <div className="submit-user-info" onClick={() => okClick()}>Save</div>
                        </div>
                      </div>
                    )}
                    {!isEdit && (
                      <div>
                        <div style={{fontWeight:"normal"}}>
                          <label>
                            <svg width="30" height="30" fill="currentColor" viewBox="0 -10 30 30">
                              <g stroke="none" stroke-width="1" fill="none" fill-rule="evenodd">
                                <g fill="black" fill-rule="nonzero">
                                   <path d="M18.75,3 C20.5449254,3 22,4.45507456 22,6.25 L22,17.75 C22,19.5449254 
                                     20.5449254,21 18.75,21 L5.25,21 C3.45507456,21 2,19.5449254 2,17.75 
                                     L2,6.25 C2,4.45507456 3.45507456,3 5.25,3 L18.75,3 Z M18.75,4.5 L5.25,4.5 
                                     C4.28350169,4.5 3.5,5.28350169 3.5,6.25 L3.5,17.75 C3.5,18.7164983 
                                     4.28350169,19.5 5.25,19.5 L18.75,19.5 C19.7164983,19.5 20.5,18.7164983 
                                     20.5,17.75 L20.5,6.25 C20.5,5.28350169 19.7164983,4.5 18.75,4.5 Z 
                                     M6.74835407,6.5 L17.2543007,6.5 C17.6685143,6.5 18.0043007,6.83578644 
                                     18.0043007,7.25 C18.0043007,7.62969577 17.7221468,7.94349096 17.3560713,7.99315338 
                                     L17.2543007,8 L6.74835407,8 C6.33414051,8 5.99835407,7.66421356 5.99835407,7.25 
                                     C5.99835407,6.87030423 6.28050795,6.55650904 6.64658351,6.50684662 
                                     L6.74835407,6.5 L17.2543007,6.5 L6.74835407,6.5 Z">
                                   </path>
                                </g>
                              </g>
                            </svg><span className="span-bold">First name</span><span>: {user?.firstName || "—"}</span>
                          </label><br/>
                          <label>
                            <svg width="30" height="30" fill="currentColor" viewBox="0 -10 30 30">
                               <g stroke="none" stroke-width="1" fill="none" fill-rule="evenodd">
                                <g fill="black" fill-rule="nonzero">
                                  <path d="M18.75,3 C20.5449254,3 22,4.45507456 22,6.25 L22,17.75 C22,19.5449254 
                                     20.5449254,21 18.75,21 L5.25,21 C3.45507456,21 2,19.5449254 2,17.75 
                                     L2,6.25 C2,4.45507456 3.45507456,3 5.25,3 L18.75,3 Z M18.75,4.5 L5.25,4.5 
                                     C4.28350169,4.5 3.5,5.28350169 3.5,6.25 L3.5,17.75 C3.5,18.7164983 
                                     4.28350169,19.5 5.25,19.5 L18.75,19.5 C19.7164983,19.5 20.5,18.7164983 
                                     20.5,17.75 L20.5,6.25 C20.5,5.28350169 19.7164983,4.5 18.75,4.5 Z 
                                     M6.74835407,6.5 L17.2543007,6.5 C17.6685143,6.5 18.0043007,6.83578644 
                                     18.0043007,7.25 C18.0043007,7.62969577 17.7221468,7.94349096 17.3560713,7.99315338 
                                     L17.2543007,8 L6.74835407,8 C6.33414051,8 5.99835407,7.66421356 5.99835407,7.25 
                                     C5.99835407,6.87030423 6.28050795,6.55650904 6.64658351,6.50684662 
                                     L6.74835407,6.5 L17.2543007,6.5 L6.74835407,6.5 Z">
                                  </path>
                                </g>
                               </g>
                            </svg><span className="span-bold">Last name</span><span>: {user?.lastName || "—"}</span>
                          </label><br/>
                          <label>
                            <svg width="30" height="30" fill="currentColor" viewBox="-1 -10 24 24">
                              <path d="M0 4a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2zm2-1a1 1 0 0 0-1 1v.217l7 
                                       4.2 7-4.2V4a1 1 0 0 0-1-1zm13 2.383-4.708 2.825L15 11.105zm-.034 6.876-5.64-3.471L8 
                                       9.583l-1.326-.795-5.64 3.47A1 1 0 0 0 2 13h12a1 1 0 0 0 .966-.741M1 11.105l4.708-2.897L1 5.383z"/>
                            </svg><span className="span-bold">Email</span><span>: {user?.email}</span>
                          </label><br/>
                          <label>
                            <svg width="30" height="30" fill="currentColor" viewBox="-3 -14 30 30">
                              <path d="M3.654 1.328a.678.678 0 0 0-1.015-.063L1.605 2.3c-.483.484-.661 1.169-.45 1.77a17.6 17.6 0 0 0 4.168 
                                    6.608 17.6 17.6 0 0 0 6.608 4.168c.601.211 1.286.033 1.77-.45l1.034-1.034a.678.678 0 0 0-.063-1.015l-2.307-1.794a.68.68 
                                    0 0 0-.58-.122l-2.19.547a1.75 1.75 0 0 1-1.657-.459L5.482 8.062a1.75 1.75 0 0 1-.46-1.657l.548-2.19a.68.68 0 0 
                                    0-.122-.58zM1.884.511a1.745 1.745 0 0 1 2.612.163L6.29 2.98c.329.423.445.974.315 1.494l-.547 2.19a.68.68 0 0 0 
                                    .178.643l2.457 2.457a.68.68 0 0 0 .644.178l2.189-.547a1.75 1.75 0 0 1 1.494.315l2.306 1.794c.829.645.905 1.87.163 
                                    2.611l-1.034 1.034c-.74.74-1.846 1.065-2.877.702a18.6 18.6 0 0 1-7.01-4.42 18.6 18.6 0 0 1-4.42-7.009c-.362-1.03-.037-2.137.703-2.877z"/>
                            </svg><span className="span-bold">Phone number</span><span>: {user?.phoneNumber || "—"}</span>
                            {(!phoneNumberConfirmed && phoneNumber !== null) &&(
                              <span style={{ height: "30px",borderRadius:"8px",fontSize:"16px", width:"80px", cursor: "pointer", marginLeft:"5px", border:"1px solid", padding:"3px 7px" }} onClick={confirmClick}>
                                    {"Confirm"}
                              </span>
                            )}
                          </label><br/>
                          <label>
                            <svg width="30" height="30" viewBox="0 -10 30 30" fill="none" stroke="currentColor" >
                              <rect x="3" y="7" width="7" height="13" rx="1.5" strokeWidth="1.5"/>
                              <rect x="14" y="3" width="7" height="17" rx="1.5" strokeWidth="1.5"/>
                              <path d="M6.5 10h0.01M6.5 13h0.01M6.5 16h0.01M17.5 6h0.01M17.5 9h0.01M17.5 12h0.01M17.5 15h0.01" strokeWidth="2"/>
                            </svg><span className="span-bold">Company</span><span>: {company?.name || "—"}</span>
                          </label><br/>
                          <label>
                            <svg width="30" height="30" viewBox="-3 -15 30 30" fill="none" stroke="currentColor" >
                              <path fill-rule="evenodd" d="M15.854 5.146a.5.5 0 0 1 0 .708l-3 3a.5.5 0 0 1-.708 0l-1.5-1.5a.5.5 0 0 1 
                                                      .708-.708L12.5 7.793l2.646-2.647a.5.5 0 0 1 .708 0"/>
                              <path d="M1 14s-1 0-1-1 1-4 6-4 6 3 6 4-1 1-1 1zm5-6a3 3 0 1 0 0-6 3 3 0 0 0 0 6"/>
                            </svg><span className="span-bold">Role</span><span>: {user?.roles?.find((c) => c) || "—"}</span>
                          </label>
                        </div>
                        <div className="submit-container" style={{padding:"0px 10px", margin: "20px 0px"}}>
                          <div className="cancel-user-info" style={{ visibility: "hidden" }} hidden></div>
                          <div className="submit-user-info" onClick={() => setIsOpen(false)}>Ok</div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
           </div>
          )}
        </div>
      ) : (
        <Link to="/login" className="navbar-item">
          <div className="user-nane-text">
            <div>
              <p className="user-nane-text-p" style={{fontSize: "20px"}}>Login</p>
            </div>
             <svg width="70" height="70"  viewBox="0 -4 18 32" fill="none">
              <path
                d="M15 16.5V19C15 20.1046 14.1046 21 13 21H6C4.89543 21 4 20.1046 4 19V5C4 3.89543 4.89543 3 6 
                   3H13C14.1046 3 15 3.89543 15 5V8.0625M20 12L9 12M9 12L11.5 14.5M9 12L11.5 9.5"
                stroke="#ffffffff"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </div>
        </Link>
      )}
      <div style={{color: "black"}}>
        <ConfirmPhoneNumber isOpenConfirm={isOpenConfirm} setIsOpenConfirm={setIsOpenConfirm} phoneNumber={phoneNumber} onChangeConfirm={handleConfirmChanged}/>
      </div>
    </section>
  );
}

export default Navbar;
